import { useState } from 'react'
import { CaretRight } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Sheet,
  useToast,
  type ScanMeta,
} from '@/ui'
import type { CloseIssuePayload, IssueToRunPayload } from '../commands'
import '../commands'
import type { BagView, IssueTaskView, ProcessState } from '../projection'
import { checkIssueBag, needsSecondEntry } from '../model/rules'
import { Counter, Line, ReadingField, ScanButton, type Reading } from '../components/bits'
import { useRefusal } from '../components/useText'
import { stepName } from '../components/labels'
import { ProcessScreen } from '../components/ProcessScreen'

/** F6.10 — Issue to run: the store hands the task's bags to the run, weighing each bag once. */
export function IssuePage() {
  const [taskId, setTaskId] = useState<string | null>(null)
  return (
    <ProcessScreen title="process.issue.title">
      {(state) => {
        const task = taskId ? state.issueTasks.get(taskId) : undefined
        return task ? (
          <IssueTask state={state} task={task} onBack={() => setTaskId(null)} />
        ) : (
          <TaskList state={state} onPick={setTaskId} />
        )
      }}
    </ProcessScreen>
  )
}

function TaskList({ state, onPick }: { state: ProcessState; onPick: (id: string) => void }) {
  const { t, locale } = useI18n()
  const tasks = [...state.issueTasks.values()].filter((x) => x.status === 'LOADING' && !x.closed_local)
  if (!tasks.length)
    return <EmptyState title={<BilingualLabel k="process.issue.none" layout="stacked" />} description={t('process.issue.noneBody')} />
  return (
    <Card>
      <CardHeader eyebrow={<BilingualLabel k="process.issue.task" />} />
      <CardBody className="py-0">
        <ul>
          {tasks.map((task) => (
            <li key={task.shipment_id}>
              <button
                type="button"
                onClick={() => onPick(task.shipment_id)}
                className="flex w-full items-center gap-3 border-b border-rule/70 py-3 text-left last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[15px] font-bold">
                    {task.unit_ids.map((u) => state.units.get(u)?.code ?? u).join(', ')}
                  </div>
                  <div className="text-[13px] text-muted">
                    {t('process.issue.toRun', { run: task.run_code })}
                    {task.step_code ? ` · ${stepName(state, task.step_code, locale)}` : ''}
                  </div>
                  <div className="text-[13px] tabular">
                    {t('process.issue.progress', { n: task.issued.size, m: task.container_ids.length })}
                  </div>
                </div>
                <CaretRight aria-hidden="true" className="text-muted" />
              </button>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}

interface Pending {
  bag: BagView
  qr: string
  entry: ScanMeta['entry']
}

function IssueTask({ state, task, onBack }: { state: ProcessState; task: IssueTaskView; onBack: () => void }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const refusal = useRefusal()
  const [pending, setPending] = useState<Pending | null>(null)
  const [reading, setReading] = useState<Reading | null>(null)
  const [moisture, setMoisture] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmDone, setConfirmDone] = useState(false)

  const step = task.step_code ? state.steps.get(task.step_code) : undefined
  const needsMoisture = !!step?.needs_moisture
  const kg = [...task.issued.values()].reduce<number>((a, g) => a + (g ?? 0), 0)
  const missing = task.container_ids.filter((c) => !task.issued.has(c))
  const issuedUnits = new Set(
    [...task.issued.keys()].map((c) => state.bags.get(c)?.unit_id).filter((x): x is string => !!x),
  )
  const moistureMissing = needsMoisture && [...issuedUnits].some((u) => !task.moisture_unit_ids.has(u))
  const pendingTare = pending?.bag.tare_g ?? 0
  const sackName = pending?.bag.sack_type_id
    ? state.pack.sack_types.find((s) => s.id === pending.bag.sack_type_id)?.name
    : null
  const askMoisture = !!pending && !task.moisture_unit_ids.has(pending.bag.unit_id)
  const m = /^(\d{1,2})(?:\.(\d))?$/.exec(moisture.trim())
  const moistureValue = m ? `${m[1]}.${m[2] ?? '0'}` : null
  const moistureOk = !askMoisture || !needsMoisture || moistureValue !== null

  const saveBag = async () => {
    if (!pending || !reading || reading.read_g <= pendingTare || !moistureOk) return
    setSaving(true)
    try {
      const item_id = newId()
      const payload: IssueToRunPayload = {
        item_id,
        shipment_id: task.shipment_id,
        run_id: task.run_id,
        container_id: pending.bag.container_id,
        qr_payload: pending.qr,
        entry: pending.entry,
        weight_read_g: reading.read_g,
        tare_g: pendingTare,
        confirmed_twice: reading.confirmed_twice,
        moisture_pct: askMoisture ? moistureValue : null,
      }
      await enqueueCommand({
        id: item_id,
        type: 'issue_to_run',
        payload,
        actorId: actor.user_id,
        parentIds: task.sync.pending_command_id ? [task.sync.pending_command_id] : [],
      })
      toast.success(t('process.issue.saved', { tag: pending.qr, kg: fmt.weight(reading.read_g - pendingTare) }))
      setPending(null)
      setReading(null)
      setMoisture('')
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  const done = async () => {
    setSaving(true)
    try {
      const payload: CloseIssuePayload = {
        shipment_id: task.shipment_id,
        bags_done: task.issued.size,
        missing_container_ids: missing,
      }
      await enqueueCommand({
        type: 'close_issue',
        payload,
        actorId: actor.user_id,
        parentIds: task.sync.pending_command_id ? [task.sync.pending_command_id] : [],
      })
      toast.success(t('process.issue.closed'))
      onBack()
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
      setConfirmDone(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="py-1">
          <Line label={t('process.issue.lots')} value={task.unit_ids.map((u) => state.units.get(u)?.code ?? u).join(', ')} />
          <Line label={t('process.issue.run')} value={task.run_code} />
        </CardBody>
      </Card>
      <div className="flex gap-2">
        <Counter
          label={<BilingualLabel k="process.issue.bags" />}
          value={task.issued.size}
          total={task.container_ids.length}
          tone={missing.length === 0 ? 'ok' : 'neutral'}
        />
        <Counter label={t('process.issue.kgSoFar')} value={Math.round(kg / 1000)} />
      </div>

      {!pending && missing.length > 0 && (
        <ScanButton
          continuous={false}
          label={<BilingualLabel k="process.issue.scan" />}
          validate={(c) => {
            const v = checkIssueBag(state, task, c)
            return v.ok ? null : refusal(v.reason)
          }}
          onScan={(c, meta) => {
            const v = checkIssueBag(state, task, c)
            if (v.ok) {
              setPending({ bag: v.value, qr: c.trim().toUpperCase(), entry: meta.entry })
              setReading(null)
              setMoisture('')
            }
          }}
        />
      )}

      {pending && (
        <Card className="border-ink">
          <CardHeader eyebrow={<BilingualLabel k="process.issue.bagTag" />} title={<span className="font-mono">{pending.qr}</span>} />
          <CardBody className="flex flex-col gap-3">
            <Line label={t('process.lot')} value={state.units.get(pending.bag.unit_id)?.code ?? '—'} />
            <Line
              label={<BilingualLabel k="process.issue.sackType" />}
              value={`${sackName ?? '—'} · ${fmt.weight(pendingTare)}`}
            />
            <ReadingField
              label={<BilingualLabel k="process.issue.weight" />}
              hint={t('process.issue.weightHint')}
              needsTwice={(g) => needsSecondEntry(g, state.pack.settings)}
              onChange={setReading}
            />
            {reading && (
              <p className="font-mono text-[15px] tabular">
                {t('process.weighOut.net', { kg: fmt.weight(reading.read_g - pendingTare) })}
              </p>
            )}
            {askMoisture && (
              <Field
                label={<BilingualLabel k="process.issue.moisture" />}
                hint={needsMoisture ? t('process.issue.moistureNeeded') : t('process.issue.moistureOnce')}
                required={needsMoisture}
              >
                <Input
                  size="touch"
                  inputMode="decimal"
                  value={moisture}
                  onChange={(e) => setMoisture(e.target.value.replace(/[^\d.]/g, '').slice(0, 4))}
                />
              </Field>
            )}
            <Button
              size="xl"
              fullWidth
              loading={saving}
              disabled={!reading || reading.read_g <= pendingTare || !moistureOk}
              onClick={() => void saveBag()}
            >
              <BilingualLabel k="process.issue.save" />
            </Button>
            <Button variant="ghost" size="touch" onClick={() => setPending(null)}>
              {t('common.cancel')}
            </Button>
          </CardBody>
        </Card>
      )}

      {!pending && (
        <>
          <p className="text-[13px] text-muted">{t('process.issue.carryHint')}</p>
          {moistureMissing && <Banner tone="warn">{t('process.issue.moistureMissing')}</Banner>}
          <Button
            size="xl"
            variant={missing.length ? 'secondary' : 'primary'}
            fullWidth
            disabled={task.issued.size === 0 || moistureMissing}
            onClick={() => (missing.length ? setConfirmDone(true) : void done())}
          >
            <BilingualLabel k="process.issue.done" />
          </Button>
          <Button variant="ghost" size="touch" onClick={onBack}>
            {t('process.issue.otherTask')}
          </Button>
        </>
      )}

      <Sheet
        open={confirmDone}
        onClose={() => setConfirmDone(false)}
        title={<BilingualLabel k="process.issue.done" />}
        description={t('process.issue.shortConfirm', { n: missing.length })}
        footer={
          <Button size="xl" fullWidth loading={saving} onClick={() => void done()}>
            <BilingualLabel k="process.issue.done" />
          </Button>
        }
      >
        <ul className="flex flex-wrap gap-2">
          {missing.map((c) => (
            <li key={c} className="rounded-chip border border-rule px-2 py-1 font-mono text-[13px]">
              {state.bags.get(c)?.qr_payload ?? c}
            </li>
          ))}
        </ul>
      </Sheet>
    </div>
  )
}
